import React, { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Bookmark, ExternalLink, Eye } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { toast } from "sonner";

const PUBLIC_LABEL = { Book: "Books", Article: "Temple Journal", Reflection: "Reflections", "Study Resource": "Additional Reading" };

export default function Article() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toggleSave, isSaved } = useApp();

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(preview ? `/admin/content/library` : `/content/library/${id}`);
        if (!ignore) setItem(preview ? (data || []).find((x) => x.id === id) : data);
      } catch {} finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [id, preview]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-14 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  if (!item) return (
    <div className="max-w-3xl mx-auto px-4 py-14 text-center">
      <div className="font-serif text-2xl mb-2" style={{ color: "var(--burgundy)" }}>This piece is not available.</div>
      <Link to="/read" className="ink-link text-sm" style={{ color: "var(--burgundy)" }}>Return to Read →</Link>
    </div>
  );

  const date = item.released_at || (item.created_at || "").slice(0, 10);
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 lg:py-16" data-testid="article-page">
      <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
        <Link to="/read" data-testid="article-back-link" className="inline-flex items-center gap-1 text-sm ink-link" style={{ color: "var(--burgundy)" }}><ArrowLeft className="w-4 h-4" /> Back to Read</Link>
        {preview && <span data-testid="article-preview-banner" className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" style={{ background: "var(--gold)", color: "var(--burgundy)" }}><Eye className="w-3 h-3" /> Admin preview · {item.status}</span>}
      </div>
      <header className="mb-8">
        <div className="eyebrow mb-3">{PUBLIC_LABEL[item.type] || item.type}{item.length ? ` · ${item.length}` : ""}</div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight" style={{ color: "var(--burgundy)" }} data-testid="article-title">{item.title}</h1>
        {item.subtitle && <p className="font-serif italic text-xl mt-3" style={{ color: "var(--text-muted)" }}>{item.subtitle}</p>}
        <div className="text-sm mt-4" style={{ color: "var(--text-muted)" }}>By {item.author}{date ? ` · ${date}` : ""}</div>
      </header>
      {item.art_url && <img src={assetUrl(item.art_url)} alt={item.title} className="w-full rounded-3xl object-cover max-h-[28rem] mb-10" data-testid="article-image" />}
      {item.excerpt && <p className="font-serif italic text-xl sm:text-2xl leading-snug mb-8 pl-5 border-l-2" style={{ color: "var(--text-obsidian)", borderColor: "var(--gold)" }}>{item.excerpt}</p>}
      <Markdown className="text-[1.05rem]" style={{ color: "var(--text-obsidian)" }}>{item.body}</Markdown>
      <div className="hairline my-10" />
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="article-save-btn" onClick={() => { toggleSave(item, "library"); toast("Saved to My Temple"); }} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
          <Bookmark className={`w-4 h-4 ${isSaved(item.id) ? "fill-current" : ""}`} /> Save to My Temple
        </button>
        {item.external_url && <a href={item.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm ink-link" style={{ color: "var(--burgundy)" }}><ExternalLink className="w-4 h-4" /> Related link</a>}
      </div>
    </article>
  );
}
